// Components
import {LandingTopBar} from '@tempo/landing';
import {
	TopBarLogo,
	TopBarList,
	TopBarActions,
	TopBarDeezerLogo,
	Box,
	Text,
	type TextProps,
} from '@tempo/core';
import {HeaderBurgerMenu} from './header-burger-menu';
import {HeaderLoginBtn, HeaderRegisterBtn} from './header-login-btn';
import {HeaderOpenPlayer} from './header-open-player';
import NextLink, {type LinkProps} from 'next/link';

// Hooks
import {useLocation} from '@app/hooks/use-location';
import {useUser} from '@deezer/react-user';
import {useTranslation} from '@app/hooks/use-translation';
import {useTracking} from '@app/modules/tracking';
import {usePathname} from 'next/navigation';

export const TopbarExposedLink = ({
	children,
	...props
}: TextProps & LinkProps) => (
	<Text
		as={NextLink}
		fontWeight="bold"
		_hover={{color: 'text.accent.primary.hovered'}}
		variant="default"
		display={{base: 'none', md: 'inline-flex'}}
		{...props}
	>
		{children}
	</Text>
);

type HeaderProps = {
	hasNavigation?: boolean;
	hasActions?: boolean;
	redirectUri?: string;
};

const Header: React.FC<HeaderProps> = ({
	hasNavigation = true,
	hasActions = true,
	redirectUri,
}) => {
	const {getDeezerUrl} = useLocation();
	const {t} = useTranslation();
	const [user] = useUser();
	const {trackClick} = useTracking();
	const pathname = usePathname();

	return (
		<Box
			borderBottom={{base: 'none', sm: 'solid 1px'}}
			borderBottomColor="divider.neutral.primary.default !important"
			width="100%"
		>
			<LandingTopBar data-cy="header">
				<TopBarLogo>
					<NextLink
						href={getDeezerUrl('/', {withQuery: false})}
						data-cy="deezer-logo"
					>
						<TopBarDeezerLogo />
					</NextLink>
				</TopBarLogo>
				{hasNavigation && (
					<TopBarList justifyContent="right">
						<TopbarExposedLink
							href={getDeezerUrl('/offers')}
							onClick={trackClick({
								eventaction: 'offers-link',
								eventcategory: 'header',
							})}
						>
							{t('footer_title_plans_standalone-features')}
						</TopbarExposedLink>
						<TopbarExposedLink
							color={
								pathname?.includes('/features')
									? 'text.accent.primary.default'
									: 'text.neutral.primary.default'
							}
							href="/features"
							onClick={trackClick({
								eventaction: 'features-link',
								eventcategory: 'header',
							})}
						>
							{t('Fonctionnalités')}
						</TopbarExposedLink>
						<TopbarExposedLink
							href={getDeezerUrl('/channels/explore/')}
							onClick={trackClick({
								eventaction: 'channels-link',
								eventcategory: 'header',
							})}
						>
							{t('Musique')}
						</TopbarExposedLink>
					</TopBarList>
				)}
				{hasActions && (
					<TopBarActions>
						{user?.isConnected() ? (
							<HeaderOpenPlayer
								display={{base: 'none', sm: 'inline-flex'}}
								size="small"
							/>
						) : (
							<>
								<HeaderLoginBtn
									redirectUri={redirectUri}
									display={{base: 'none', sm: 'inline-flex'}}
									size="small"
								/>
								<HeaderRegisterBtn
									redirectUri={redirectUri}
									display={{base: 'none', sm: 'inline-flex'}}
									size="small"
								/>
							</>
						)}
						<HeaderBurgerMenu
							redirectUri={redirectUri}
							display={{base: 'inline-flex', sm: 'none'}}
						/>
					</TopBarActions>
				)}
			</LandingTopBar>
		</Box>
	);
};

export default Header;
